/**
 * Shared Claude facilitation API helper.
 * All session phase components use this to call the Edge Function.
 */
import i18n from 'i18next'
import type { SessionMessage, SessionMode } from '../types/session'
import { getVariationIndex } from '../data/promptVariations'
import { codeForFailure } from './guideErrors'
import { functionHeaders } from './functionAuth'
import type { GuideErrorCode } from './guideErrors'

export interface ClaudeCallOptions {
  phase: number
  emotion: string
  intensity: number
  messages: SessionMessage[]
  firstName?: string
  intentionText?: string
  /** Pass sessionId so variation index is deterministic per session */
  sessionId?: string
  /** Numerology life path number derived from user's birthday */
  lifePathNumber?: number
  /** For phase 20 (option generation): context describing what options to generate */
  optionContext?: string
  /** For phase 11 (cross-session synthesis): pattern_reflection strings from all past completed sessions */
  sessionPatterns?: string[]
  /** For series sessions: arc name, session position, and focus description to steer the AI */
  seriesContext?: string
  /**
   * Awakening-only: short summary of recurring threads from past sessions
   * (cluster labels). Injected into phases 1, 3, 4 as background awareness
   * for the guide — never referenced directly to the user.
   */
  clusterContext?: string
  /**
   * Mind-level lens picked at Arrival. Switches the system prompt for phases
   * 2 (Feel → RAIN), 3 (See → The Work), 4 (Shift → turnaround). Defaults to
   * 'standard' on the server when omitted.
   */
  sessionMode?: SessionMode
  /**
   * For phase 2 (the Feel reflection): what the person wrote on the later Feel
   * screens. A key is present only if they wrote something.
   */
  feelDetails?: { sensations?: string; belief?: string; expression?: string }
  /**
   * The person's own words for the age of the feeling (phases 3, 4 and 6), or
   * absent when they gave none. See lib/age.ts for what counts as an answer.
   */
  ageText?: string
}

/**
 * The language the guide must answer in: the one the person is reading.
 *
 * Deliberately NOT a call option. It used to be one, and each of roughly
 * thirty call sites had to remember to pass it. Two did. Everything else
 * reached the server with no language, which the server reads as English, so a
 * Polish reader got English sentence starters, feeling chips and archetype
 * cards inside an otherwise Polish screen.
 *
 * Reading it here means a new call site cannot get it wrong. The UI language
 * is the right source: useLanguage() applies the stored profile preference to
 * i18n at sign-in, so this follows the profile and also the screen the person
 * is actually looking at. Regional codes ("pl-PL") are cut to the base code,
 * which is what the server's language table is keyed by.
 */
function guideLanguage(): string {
  return (i18n.language ?? 'en').split('-')[0] || 'en'
}

export type ClaudeResult =
  | { response: string; crisis?: boolean }
  | { error: string; code: GuideErrorCode }

/** Strip markdown that Claude occasionally outputs despite plain-text instructions */
function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')             // headers
    .replace(/^\s*---+\s*$/gm, '')            // horizontal rules
    .replace(/^\|.*\|.*$/gm, '')              // table rows (pipe-delimited)
    .replace(/^[-*]\s+/gm, '')               // bullet list markers
    .replace(/^>\s+/gm, '')                  // blockquotes
    .replace(/\*\*(.*?)\*\*/g, '$1')          // bold asterisk
    .replace(/__(.*?)__/g, '$1')              // bold underscore
    .replace(/\*(.*?)\*/g, '$1')              // italic asterisk
    .replace(/_(.*?)_/g, '$1')               // italic underscore
    .replace(/`(.*?)`/g, '$1')               // inline code
    .replace(/\n{3,}/g, '\n\n')              // collapse excess blank lines
    .trim()
}

/**
 * How long to wait for the guide before giving up, in milliseconds.
 *
 * Without a limit a request that never comes back leaves the person on a
 * waiting screen, and a waiting screen deliberately answers Back with nothing
 * (there is no finished screen to go back from), so they would have no way out
 * but closing the app. Giving up turns the wait into the ordinary error result,
 * which every phase already shows as "something went wrong" with Try again and
 * Back.
 *
 * 60 seconds: the slowest replies (a full session summary, the longest card)
 * take well under half that, so a healthy call is never cut short, and it is
 * still short enough that nobody thinks the app has died.
 */
export const GUIDE_TIMEOUT_MS = 60_000

export async function callClaude(opts: ClaudeCallOptions): Promise<ClaudeResult> {
  const {
    phase,
    emotion,
    intensity,
    messages,
    firstName = '',
    intentionText = '',
    sessionId = '',
    lifePathNumber,
    optionContext,
    sessionPatterns,
    seriesContext,
    clusterContext,
    sessionMode,
    feelDetails,
    ageText,
  } = opts

  const lang = guideLanguage()
  const variationIndex = sessionId ? getVariationIndex(sessionId) : 0

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), GUIDE_TIMEOUT_MS)

  try {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/facilitate`,
      {
        method: 'POST',
        signal: controller.signal,
        // The person's own token: the function works out who is calling from it (#72).
        headers: await functionHeaders(),
        body: JSON.stringify({
          phase,
          emotion,
          intensity,
          messages,
          firstName,
          intentionText,
          variationIndex,
          sessionId,
          lifePathNumber,
          optionContext,
          sessionPatterns,
          seriesContext,
          clusterContext,
          sessionMode,
          feelDetails,
          ageText,
          lang,
        }),
      }
    )
    // A reply that is not JSON (a gateway's HTML error page) is a failure of the
    // server, not of the connection, so it must not throw into the 'network' branch.
    const json = await res.json().catch(() => null)
    if (!res.ok) return { error: `${res.status} — ${json?.error ?? JSON.stringify(json)}`, code: codeForFailure(res.status, json?.error) }
    if (!json?.response) return { error: 'Empty response from guide', code: 'empty' }
    return { response: stripMarkdown(json.response as string), ...(json.crisis ? { crisis: true } : {}) }
  } catch (err) {
    if (controller.signal.aborted) return { error: 'The guide did not answer in time. Please try again.', code: 'timeout' }
    return { error: String(err), code: 'network' }
  } finally {
    // The timer covers reading the reply as well as waiting for it to start.
    clearTimeout(timer)
  }
}
